
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import '../quotes/quote-generator.css';
import './invoice-generator.css';
import { apiRequest, downloadApiFile } from '../../../api/client';
import { exportElementToPdf } from '../../../utils/exportPdf';

interface ClientInfo {
    firstName: string;
    lastName: string;
    buildingName: string;
    propertyAddress: string;
    phone: string;
    email: string;
}

interface ExistingClient {
    _id: string;
    firstName?: string;
    lastName?: string;
    buildingName?: string;
    address?: string;
    phone?: string;
    email?: string;
}

interface LineItem {
    id: string;
    item: string;
    description: string;
    quantity: number;
    unitPrice: number;
}

interface LineItemCatalogSuggestion {
    item: string;
    description: string;
}

const toIsoDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const addDaysIso = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return toIsoDate(date);
};

const formatIsoForDisplay = (isoDate: string): string => {
    if (!isoDate) return 'N/A';
    const parsed = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleDateString();
};

const normalizeIssuedDate = (issuedDate: string | undefined): string => {
    if (!issuedDate || issuedDate === 'today' || issuedDate === 'date_sent' || issuedDate === 'custom') {
        return toIsoDate(new Date());
    }
    return issuedDate;
};

const displayNumberInput = (value: number): string | number => (value === 0 ? '' : value);
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value.trim());
const isValidPhoneNumber = (value: string): boolean => {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1'));
};

/** Ensure every line item has a unique `id` — items loaded from the DB lack one. */
const ensureLineItemIds = (items: unknown): LineItem[] => {
    if (!Array.isArray(items)) return [];
    return items.map((raw, index) => ({
        ...raw,
        id: raw.id || `${Date.now()}-${index}`
    }));
};
const COMPANY_NAME = 'Zera Fire Protection';
const COMPANY_ADDRESS = '27 Hemlock Crt, Barrie L4N9N5 Canada';
const COMPANY_PHONE = '+1 (437) 898-8866';
const COMPANY_EMAIL = 'info@zerafireprotection.com';
const COMPANY_WEBSITE = 'www.zerafireprotection.com';
const COMPANY_HST_GST = 'HST/GST #: 771473626RT0001';
const DEFAULT_ZERA_PDF_LOGO = '/assets/branding/zera-transparent.png';

export default function InvoiceGenerator(): React.JSX.Element {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(null);
    const [currentInvoiceSerial, setCurrentInvoiceSerial] = useState<number | null>(null);

    const [clientInfo, setClientInfo] = useState<ClientInfo>({
        firstName: '',
        lastName: '',
        buildingName: '',
        propertyAddress: '',
        phone: '',
        email: ''
    });

    const [invoiceTitle, setInvoiceTitle] = useState('For Services Rendered');
    const [issuedDate, setIssuedDate] = useState(toIsoDate(new Date()));
    const [paymentDue, setPaymentDue] = useState('net_30');
    const [dueDate, setDueDate] = useState(addDaysIso(30));
    const [salesperson, setSalesperson] = useState('');

    const [lineItems, setLineItems] = useState<LineItem[]>([]);

    const [discount, setDiscount] = useState(0);
    const [tax, setTax] = useState(0);
    const [clientMessage, setClientMessage] = useState('');
    const [disclaimer] = useState('Thank you for your business. Please contact us with any questions regarding this invoice.');

    const [showPreview, setShowPreview] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPreparingPdf, setIsPreparingPdf] = useState(false);
    const [showClientPicker, setShowClientPicker] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [existingClients, setExistingClients] = useState<ExistingClient[]>([]);
    const [isClientSearchLoading, setIsClientSearchLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [zeraStylePdfLogoSrc, setZeraStylePdfLogoSrc] = useState(DEFAULT_ZERA_PDF_LOGO);

    useEffect(() => {
        apiRequest<{ branding?: { logoBase64?: string } | null }>('/api/branding')
            .then((res) => {
                const b64 = (res.branding?.logoBase64 || '').trim();
                if (b64) setZeraStylePdfLogoSrc(b64);
            })
            .catch(() => {});
    }, []);

    const [itemSuggest, setItemSuggest] = useState<{
        lineId: string;
        list: LineItemCatalogSuggestion[];
        loading: boolean;
    } | null>(null);
    const itemSuggestFetchSeq = useRef(0);
    const itemSuggestDebounceRef = useRef<number | null>(null);

    useEffect(
        () => () => {
            if (itemSuggestDebounceRef.current) {
                window.clearTimeout(itemSuggestDebounceRef.current);
            }
        },
        []
    );

    const scheduleLineItemSuggest = (lineId: string, query: string) => {
        if (itemSuggestDebounceRef.current) {
            window.clearTimeout(itemSuggestDebounceRef.current);
        }
        const t = query.trim();
        if (t.length < 1) {
            setItemSuggest(null);
            return;
        }
        itemSuggestDebounceRef.current = window.setTimeout(() => {
            const seq = ++itemSuggestFetchSeq.current;
            setItemSuggest({ lineId, list: [], loading: true });
            apiRequest<{ data: LineItemCatalogSuggestion[] }>(
                `/api/catalog/line-items?q=${encodeURIComponent(t)}&limit=30`
            )
                .then((res) => {
                    if (seq !== itemSuggestFetchSeq.current) return;
                    setItemSuggest({ lineId, list: res.data || [], loading: false });
                })
                .catch(() => {
                    if (seq !== itemSuggestFetchSeq.current) return;
                    setItemSuggest({ lineId, list: [], loading: false });
                });
        }, 250);
    };

    const applyLineItemSuggestion = (lineId: string, suggestion: LineItemCatalogSuggestion) => {
        itemSuggestFetchSeq.current += 1;
        setItemSuggest(null);
        setLineItems((prev) =>
            prev.map((row) =>
                row.id === lineId
                    ? { ...row, item: suggestion.item, description: suggestion.description || '' }
                    : row
            )
        );
    };

    const closeLineItemSuggest = () => {
        itemSuggestFetchSeq.current += 1;
        setItemSuggest(null);
    };

    // Calculations
    const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const discountAmount = (subtotal * discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * tax) / 100;
    const total = taxableAmount + taxAmount;

    useEffect(() => {
        const invoiceId = searchParams.get('id');
        if (!invoiceId) return;

        apiRequest<{ data: any }>(`/api/invoices/${invoiceId}`)
            .then((data) => {
                const existing = data.data;
                setCurrentInvoiceId(existing._id);
                setCurrentInvoiceSerial(typeof existing.serialNumber === 'number' ? existing.serialNumber : null);
                if (existing.meta) {
                    const meta = existing.meta;
                    setClientInfo(meta.clientInfo || {
                        firstName: '',
                        lastName: '',
                        buildingName: '',
                        propertyAddress: '',
                        phone: '',
                        email: ''
                    });
                    setInvoiceTitle(meta.invoiceTitle || 'For Services Rendered');
                    setIssuedDate(normalizeIssuedDate(meta.issuedDate));
                    setPaymentDue(meta.paymentDue || 'net_30');
                    setDueDate(
                        meta.dueDate ||
                        (meta.paymentDue === 'net_15'
                            ? addDaysIso(15)
                            : meta.paymentDue === 'due_on_receipt'
                                ? toIsoDate(new Date())
                                : addDaysIso(30))
                    );
                    setSalesperson(meta.salesperson || '');
                    setLineItems(ensureLineItemIds(meta.lineItems));
                    setDiscount(meta.discount ?? 0);
                    setTax(meta.tax ?? 0);
                    setClientMessage(meta.clientMessage || '');
                } else {
                    setClientInfo({
                        firstName: existing.contact?.name?.split(' ')[0] || '',
                        lastName: existing.contact?.name?.split(' ').slice(1).join(' ') || '',
                        buildingName: (existing.meta as any)?.clientInfo?.buildingName || '',
                        propertyAddress: existing.contact?.address || '',
                        phone: existing.contact?.phone || '',
                        email: existing.contact?.email || ''
                    });
                    setInvoiceTitle(existing.invoiceNumber || 'For Services Rendered');
                    setLineItems(ensureLineItemIds(existing.lineItems));
                    setDiscount(0);
                    setTax(existing.taxPercent || 0);
                    setDueDate(addDaysIso(30));
                }
            })
            .catch(() => {
                setCurrentInvoiceId(null);
                setErrorMessage('Failed to load existing invoice. You can continue creating a new one.');
            });
    }, [searchParams]);

    useEffect(() => {
        if (!showClientPicker) return;

        const query = clientSearch.trim();
        const timer = window.setTimeout(() => {
            setIsClientSearchLoading(true);
            apiRequest<{ data: ExistingClient[] }>(`/api/clients?q=${encodeURIComponent(query)}&limit=50`)
                .then((res) => setExistingClients(res.data || []))
                .catch(() => setExistingClients([]))
                .finally(() => setIsClientSearchLoading(false));
        }, 250);

        return () => window.clearTimeout(timer);
    }, [showClientPicker, clientSearch]);

    const saveInvoice = async (
        status: 'Draft' | 'Sent',
        email?: string
    ): Promise<{ id: string; serialNumber: number | null }> => {
        const payload: Record<string, unknown> = {
            invoiceNumber: invoiceTitle || 'Draft Invoice',
            status: status === 'Sent' ? 'sent' : 'draft',
            contact: {
                name: `${clientInfo.firstName} ${clientInfo.lastName}`.trim(),
                email: email || clientInfo.email,
                phone: clientInfo.phone,
                address: clientInfo.propertyAddress
            },
            lineItems,
            subtotal,
            taxPercent: tax,
            total,
            meta: {
                clientInfo,
                invoiceTitle,
                issuedDate,
                paymentDue,
                dueDate,
                salesperson,
                lineItems,
                discount,
                tax,
                clientMessage,
                disclaimer
            }
        };

        // Include editable serial number if set
        if (currentInvoiceSerial != null) {
            payload.serialNumber = currentInvoiceSerial;
        }

        if (currentInvoiceId) {
            const updated = await apiRequest<{ data: any }>(`/api/invoices/${currentInvoiceId}`, {
                method: 'PATCH',
                json: payload
            });
            const serialNumber = typeof updated.data.serialNumber === 'number' ? updated.data.serialNumber : null;
            setCurrentInvoiceSerial(serialNumber);
            return { id: currentInvoiceId, serialNumber };
        } else {
            const created = await apiRequest<{ data: any }>('/api/invoices', {
                method: 'POST',
                json: payload
            });
            setCurrentInvoiceId(created.data._id);
            const serialNumber = typeof created.data.serialNumber === 'number' ? created.data.serialNumber : null;
            setCurrentInvoiceSerial(serialNumber);
            return { id: created.data._id, serialNumber };
        }
    };

    // Handlers
    const handleAddLineItem = () => {
        const newItem: LineItem = {
            id: Date.now().toString(),
            item: '',
            description: '',
            quantity: 1,
            unitPrice: 0
        };
        setLineItems([...lineItems, newItem]);
    };

    const handleRemoveLineItem = (id: string) => {
        setLineItems(lineItems.filter(item => item.id !== id));
    };

    const handleLineItemChange = (id: string, field: keyof LineItem, value: any) => {
        setLineItems(lineItems.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const handleSelectExistingClient = (selected: ExistingClient) => {
        setClientInfo({
            firstName: selected.firstName || '',
            lastName: selected.lastName || '',
            buildingName: selected.buildingName || '',
            propertyAddress: selected.address || '',
            phone: selected.phone || '',
            email: selected.email || ''
        });
        setShowClientPicker(false);
        setClientSearch('');
    };

    const validateForPdf = (): string[] => {
        const missing: string[] = [];

        if (!clientInfo.firstName.trim()) missing.push('Client first name');
        if (!clientInfo.lastName.trim()) missing.push('Client last name');
        if (!clientInfo.email.trim()) missing.push('Client email');
        if (clientInfo.email.trim() && !isValidEmail(clientInfo.email)) missing.push('Client email must be valid');
        if (!clientInfo.phone.trim()) missing.push('Client phone number');
        if (clientInfo.phone.trim() && !isValidPhoneNumber(clientInfo.phone)) {
            missing.push('Client phone must be 10 digits (or 11 digits starting with 1, e.g. +1)');
        }
        if (!invoiceTitle.trim()) missing.push('Invoice title');
        if (!issuedDate) missing.push('Issue date');
        if (!dueDate) missing.push('Due date');
        if (!salesperson.trim()) missing.push('Salesperson');
        if (lineItems.length === 0) missing.push('At least one line item');

        const hasInvalidLineItem = lineItems.some(
            (item) => !item.item.trim() || item.quantity <= 0 || item.unitPrice < 0
        );
        if (hasInvalidLineItem) {
            missing.push('Line items must include item name, quantity > 0, and valid unit price');
        }

        return missing;
    };

    const canGeneratePdf = (): boolean => {
        const missing = validateForPdf();
        if (missing.length > 0) {
            setErrorMessage(`Please complete required fields before generating PDF:\n- ${missing.join('\n- ')}`);
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setIsSaving(true);
        try {
            await saveInvoice('Draft');
            setSuccessMessage('Invoice saved successfully.');
            navigate('/admin/invoices');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to save invoice.';
            setErrorMessage(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReviewAndSend = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!canGeneratePdf()) return;
        try {
            setIsSaving(true);
            await saveInvoice('Draft');
            setConfirmEmail(clientInfo.email);
            setShowPreview(true);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to prepare invoice preview.';
            setErrorMessage(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendEmail = () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!canGeneratePdf()) return;
        if (!confirmEmail || !isValidEmail(confirmEmail)) {
            setErrorMessage('Please enter a valid email address.');
            return;
        }

        setIsSending(true);
        saveInvoice('Sent', confirmEmail)
            .then(({ id }) =>
                apiRequest('/api/invoices/send', {
                    method: 'POST',
                    json: { invoiceId: id, email: confirmEmail }
                })
            )
            .then(() => {
                setIsSending(false);
                setShowPreview(false);
                setSuccessMessage(`Invoice successfully sent to ${confirmEmail}!`);
                navigate('/admin/invoices');
            })
            .catch((error: unknown) => {
                setIsSending(false);
                const message = error instanceof Error ? error.message : 'Failed to send invoice.';
                setErrorMessage(message);
            });
    };

    const handleDownloadPdf = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!canGeneratePdf()) return;
        try {
            setIsDownloading(true);
            const { id } = await saveInvoice('Draft');
            const brandingRes = await apiRequest<{
                branding?: { pdfVariant?: string; useClientRasterExport?: boolean } | null;
            }>('/api/branding').catch(() => ({ branding: null }));
            const useZeraDesign = brandingRes.branding?.useClientRasterExport === true;

            if (useZeraDesign) {
                const openedForDownload = !showPreview;
                if (openedForDownload) {
                    setConfirmEmail(clientInfo.email);
                    setIsPreparingPdf(true);
                    setShowPreview(true);
                    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
                    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
                }

                const previewElement = document.querySelector('.modal-content .pdf-page') as HTMLElement | null;
                if (!previewElement) {
                    throw new Error('Preview element not found.');
                }

                await exportElementToPdf(previewElement, invoiceTitle || 'Invoice');
                if (openedForDownload) {
                    setShowPreview(false);
                    setIsPreparingPdf(false);
                }
            } else {
                const fileNameBase = (invoiceTitle || 'Invoice').trim() || 'Invoice';
                await downloadApiFile(`/api/invoices/pdf?invoiceId=${encodeURIComponent(id)}`, `${fileNameBase}.pdf`);
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to download invoice PDF.';
            setErrorMessage(message);
        } finally {
            setIsDownloading(false);
        }
    };

    const fmtCurrency = (val: number) => `$${val.toFixed(2)}`;
    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container">
                    <header className="quote-header" style={{ marginBottom: '18px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.3rem', marginBottom: '8px' }}>
                                {currentInvoiceId ? 'Edit Invoice' : 'New Invoice'}
                            </h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '0.98rem' }}>
                                {currentInvoiceId
                                    ? `Editing Invoice #${currentInvoiceSerial ?? '—'}`
                                    : 'Generate a professional invoice for your client.'}
                            </p>
                        </div>
                        <button
                            className="btn-icon"
                            onClick={() => navigate('/admin/invoices')}
                            aria-label="Back to invoices"
                        >
                            ←
                        </button>
                    </header>

                {errorMessage && (
                    <div className="banner banner-error">
                        {errorMessage.split('\n').map((line) => (
                            <div key={line}>{line}</div>
                        ))}
                    </div>
                )}
                {successMessage && (
                    <div className="banner banner-success">
                        {successMessage}
                    </div>
                )}
                {/* Review & Send Modal */}
                {showPreview && (
                    <div
                        className={`modal-overlay${isPreparingPdf ? ' modal-overlay-hidden' : ''}`}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Review and Send Invoice</h2>
                                <button className="btn-icon" onClick={() => setShowPreview(false)}>✕</button>
                            </div>
                            <div className="modal-body">
                                <div className="email-confirm-panel">
                                    <h3>Recipient Details</h3>
                                    <p className="panel-info">
                                        Confirm the client's email address. A PDF copy will be attached.
                                    </p>
                                    <div className="form-group">
                                        <label className="form-label">Client Email</label>
                                        <input
                                            className="form-input"
                                            type="email"
                                            value={confirmEmail}
                                            onChange={e => setConfirmEmail(e.target.value)}
                                            placeholder="client@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="pdf-preview-container">
                                    <div className="pdf-page" data-pdf-exporting="true">
                                        <div className="pdf-brand-header" data-pdf-header>
                                            <div className="pdf-brand-left">
                                                <img className="pdf-brand-logo-img" src={zeraStylePdfLogoSrc} alt="Company logo" />
                                            </div>
                                            <div className="pdf-brand-right">
                                                <img className="pdf-brand-member-cfaa" src="/assets/branding/cfaa.png" alt="CFAA member" />
                                                <img className="pdf-brand-member-acmo" src="/assets/branding/acmo.png" alt="ACMO member" />
                                            </div>
                                        </div>
                                        <div className="pdf-body-content">
                                            <div className="invoice-header">
                                                <div>
                                                    <div className="invoice-title">INVOICE</div>
                                                    <div style={{ marginTop: '10px', color: '#666' }}>
                                                        Invoice #{currentInvoiceSerial ?? '—'}
                                                    </div>
                                                </div>
                                                <div className="company-details">
                                                    <strong>{COMPANY_NAME}</strong><br />
                                                    {COMPANY_ADDRESS}<br />
                                                    {COMPANY_PHONE}<br />
                                                    {COMPANY_EMAIL}<br />
                                                    {COMPANY_WEBSITE}<br />
                                                    {COMPANY_HST_GST}
                                                </div>
                                            </div>

                                            <div className="invoice-meta-grid">
                                                <div className="meta-group">
                                                    <h3>Bill To</h3>
                                                    <div className="meta-content">
                                                        {clientInfo.firstName} {clientInfo.lastName}<br />
                                                        {clientInfo.buildingName ? <>{clientInfo.buildingName}<br /></> : null}
                                                        {clientInfo.propertyAddress || 'No Address'}<br />
                                                        {clientInfo.email}<br />
                                                        {clientInfo.phone}
                                                    </div>
                                                </div>
                                                <div className="meta-group">
                                                    <h3>Invoice Details</h3>
                                                    <div className="meta-content">
                                                        <strong>{invoiceTitle}</strong><br />
                                                        Issued: {formatIsoForDisplay(issuedDate)}<br />
                                                        Due: {formatIsoForDisplay(dueDate)}<br />
                                                        Salesperson: {salesperson || 'N/A'}<br />
                                                        {COMPANY_HST_GST}
                                                    </div>
                                                </div>
                                            </div>

                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Item</th>
                                                        <th>Qty</th>
                                                        <th>Rate</th>
                                                        <th>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lineItems.map(item => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <strong>{item.item}</strong>
                                                                <div
                                                                    style={{
                                                                        fontSize: '0.85rem',
                                                                        color: '#888',
                                                                        whiteSpace: 'pre-wrap',
                                                                        lineHeight: 1.45
                                                                    }}
                                                                >
                                                                    {item.description}
                                                                </div>
                                                            </td>
                                                            <td>{item.quantity}</td>
                                                            <td>{fmtCurrency(item.unitPrice)}</td>
                                                            <td>{fmtCurrency(item.quantity * item.unitPrice)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            <div className="invoice-totals">
                                                <div className="total-row">
                                                    <span>Subtotal</span>
                                                    <span>{fmtCurrency(subtotal)}</span>
                                                </div>
                                                {discount > 0 && (
                                                    <div className="total-row" style={{ color: '#d4492c' }}>
                                                        <span>Discount ({discount}%)</span>
                                                        <span>-{fmtCurrency(discountAmount)}</span>
                                                    </div>
                                                )}
                                                <div className="total-row">
                                                    <span>Tax ({tax}%)</span>
                                                    <span>{fmtCurrency(taxAmount)}</span>
                                                </div>
                                                <div className="total-row final">
                                                    <span>Total</span>
                                                    <span>{fmtCurrency(total)}</span>
                                                </div>
                                            </div>

                                            <div className="pdf-fixed-footer" data-pdf-footer>
                                                <div className="invoice-footer-note">
                                                    {disclaimer}
                                                    <br />
                                                    {COMPANY_NAME} • {COMPANY_WEBSITE}
                                                </div>

                                                <div className="invoice-compliance-footer">
                                                    Services performed in accordance with CAN/ULC Standards and Ontario Fire Code.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn-secondary" onClick={() => setShowPreview(false)}>
                                    Cancel
                                </button>
                                <button className="btn-secondary" onClick={handleDownloadPdf} disabled={isDownloading || isSending}>
                                    {isDownloading ? 'Downloading...' : 'Download PDF'}
                                </button>
                                <button className="btn-primary" onClick={handleSendEmail} disabled={isSending}>
                                    {isSending ? 'Sending...' : 'Send Invoice & Email'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Billed To Section */}
                    <section className="quote-card">
                        <div className="invoice-card-header">
                            <div className="invoice-icon-wrapper invoice-icon-client">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="invoice-card-title">Billed To</h2>
                                <p className="invoice-card-subtitle">Enter client information</p>
                            </div>
                        </div>

                        <button
                            className="invoice-select-client-btn"
                            type="button"
                            onClick={() => setShowClientPicker((prev) => !prev)}
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
                                <path d="M14 14L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            {showClientPicker ? 'Hide Existing Clients' : 'Select Existing Client'}
                        </button>
                        {showClientPicker && (
                            <div style={{ border: '1px solid var(--invoice-border)', borderRadius: '12px', padding: '12px', marginBottom: '16px', background: '#fff' }}>
                                <input
                                    className="invoice-input"
                                    placeholder="Search by building name or address"
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                />
                                <div style={{ marginTop: '10px', maxHeight: '220px', overflowY: 'auto', display: 'grid', gap: '8px' }}>
                                    {isClientSearchLoading && <div style={{ color: 'var(--invoice-text-muted)', fontSize: '0.9rem' }}>Searching clients...</div>}
                                    {!isClientSearchLoading && existingClients.length === 0 && (
                                        <div style={{ color: 'var(--invoice-text-muted)', fontSize: '0.9rem' }}>No matching clients found.</div>
                                    )}
                                    {!isClientSearchLoading && existingClients.map((c) => (
                                        <button
                                            key={c._id}
                                            type="button"
                                            className="invoice-btn invoice-btn-secondary"
                                            style={{ textAlign: 'left', justifyContent: 'flex-start', padding: '10px 12px' }}
                                            onClick={() => handleSelectExistingClient(c)}
                                        >
                                            {(c.firstName || c.lastName) ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : 'Client'}
                                            {' • '}
                                            {c.buildingName || c.address || 'No address'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="invoice-form-grid">
                            <div className="invoice-form-group">
                                <label className="invoice-label">First Name</label>
                                <input
                                    type="text"
                                    className="invoice-input"
                                    placeholder="John"
                                    value={clientInfo.firstName}
                                    onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                                />
                            </div>

                            <div className="invoice-form-group">
                                <label className="invoice-label">Last Name</label>
                                <input
                                    type="text"
                                    className="invoice-input"
                                    placeholder="Doe"
                                    value={clientInfo.lastName}
                                    onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                                />
                            </div>

                            <div className="invoice-form-group invoice-full-width">
                                <label className="invoice-label">Building Name</label>
                                <input
                                    type="text"
                                    className="invoice-input"
                                    placeholder="e.g. Maple Heights Tower"
                                    value={clientInfo.buildingName || ''}
                                    onChange={(e) => setClientInfo({ ...clientInfo, buildingName: e.target.value })}
                                />
                            </div>

                            <div className="invoice-form-group invoice-full-width">
                                <label className="invoice-label">Property Address</label>
                                <input
                                    type="text"
                                    className="invoice-input"
                                    placeholder="123 Main Street, City, State 12345"
                                    value={clientInfo.propertyAddress}
                                    onChange={(e) => setClientInfo({ ...clientInfo, propertyAddress: e.target.value })}
                                />
                            </div>

                            <div className="invoice-form-group">
                                <label className="invoice-label">Phone Number</label>
                                <input
                                    type="tel"
                                    className="invoice-input"
                                    placeholder="(555) 123-4567"
                                    value={clientInfo.phone}
                                    onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                                />
                            </div>

                            <div className="invoice-form-group">
                                <label className="invoice-label">Email Address</label>
                                <input
                                    type="email"
                                    className="invoice-input"
                                    placeholder="client@example.com"
                                    value={clientInfo.email}
                                    onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Overview Section */}
                    <section className="quote-card">
                        <div className="invoice-card-header">
                            <div className="invoice-icon-wrapper invoice-icon-overview">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="invoice-card-title">Overview</h2>
                                <p className="invoice-card-subtitle">Invoice details and settings</p>
                            </div>
                        </div>

                        <div className="invoice-form-grid">
                            <div className="invoice-form-group">
                                <label className="invoice-label">Invoice #</label>
                                <input
                                    type="number"
                                    className="invoice-input"
                                    placeholder="Auto-generated"
                                    value={currentInvoiceSerial ?? ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCurrentInvoiceSerial(val === '' ? null : parseInt(val, 10) || null);
                                    }}
                                />
                                {!currentInvoiceId && !currentInvoiceSerial && (
                                    <span className="invoice-label-hint">Will be auto-assigned on first save</span>
                                )}
                            </div>


                            <div className="invoice-form-group invoice-full-width">
                                <label className="invoice-label">Invoice Title</label>
                                <input
                                    type="text"
                                    className="invoice-input"
                                    placeholder="For Services Rendered"
                                    value={invoiceTitle}
                                    onChange={(e) => setInvoiceTitle(e.target.value)}
                                />
                            </div>

                            <div className="invoice-form-group">
                                <label className="invoice-label">Issue Date</label>
                                <input
                                    type="date"
                                    className="invoice-input"
                                    value={issuedDate}
                                    onChange={(e) => setIssuedDate(e.target.value)}
                                />
                            </div>

                            <div className="invoice-form-group">
                                <label className="invoice-label">Due Date</label>
                                <input
                                    type="date"
                                    className="invoice-input"
                                    value={dueDate}
                                    onChange={(e) => {
                                        setDueDate(e.target.value);
                                        setPaymentDue('custom');
                                    }}
                                />
                            </div>

                            <div className="invoice-form-group invoice-full-width">
                                <label className="invoice-label">Salesperson</label>
                                <input
                                    type="text"
                                    className="invoice-input"
                                    value={salesperson}
                                    placeholder="Enter salesperson name"
                                    onChange={(e) => setSalesperson(e.target.value)}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Product/Service Section */}
                    <section className="quote-card">
                        <div className="invoice-card-header">
                            <div className="invoice-icon-wrapper invoice-icon-products">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M3.27002 6.96L12 12.01L20.73 6.96" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 22.08V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="invoice-card-title">Product / Service</h2>
                                <p className="invoice-card-subtitle">Add line items to this invoice</p>
                            </div>
                        </div>

                        <div className="invoice-line-items-section">
                            <div className="invoice-section-label">Line Items</div>

                            {lineItems.length > 0 && (
                                <div className="invoice-line-items">
                                    {lineItems.map((item, index) => (
                                        <div key={item.id} className="invoice-line-item">
                                            <div className="invoice-line-item-header">
                                                <span className="invoice-line-item-number">#{index + 1}</span>
                                                <button
                                                    className="invoice-remove-btn"
                                                    onClick={() => handleRemoveLineItem(item.id)}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="invoice-line-item-grid">
                                                <div className="invoice-form-group invoice-full-width">
                                                    <label className="invoice-label-sm">Item Name</label>
                                                    <div className="invoice-item-name-field">
                                                        <input
                                                            type="text"
                                                            className="invoice-input-sm"
                                                            placeholder="Product or service name"
                                                            value={item.item}
                                                            autoComplete="off"
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                handleLineItemChange(item.id, 'item', v);
                                                                scheduleLineItemSuggest(item.id, v);
                                                            }}
                                                            onFocus={() => {
                                                                if (item.item.trim()) {
                                                                    scheduleLineItemSuggest(item.id, item.item);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                window.setTimeout(() => closeLineItemSuggest(), 200);
                                                            }}
                                                        />
                                                        {itemSuggest?.lineId === item.id &&
                                                            (itemSuggest.loading || itemSuggest.list.length > 0) && (
                                                            <ul className="invoice-item-suggest-list">
                                                                {itemSuggest.loading && (
                                                                    <li className="invoice-item-suggest-muted">Searching…</li>
                                                                )}
                                                                {!itemSuggest.loading &&
                                                                    itemSuggest.list.map((s, si) => (
                                                                        <li key={`${si}-${s.item}-${s.description}`}>
                                                                            <button
                                                                                type="button"
                                                                                className="invoice-item-suggest-option"
                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                onClick={() =>
                                                                                    applyLineItemSuggestion(item.id, s)
                                                                                }
                                                                            >
                                                                                <span className="invoice-item-suggest-name">
                                                                                    {s.item}
                                                                                </span>
                                                                                <span
                                                                                    className={
                                                                                        s.description
                                                                                            ? 'invoice-item-suggest-desc'
                                                                                            : 'invoice-item-suggest-desc invoice-item-suggest-desc--empty'
                                                                                    }
                                                                                >
                                                                                    {s.description || 'No description'}
                                                                                </span>
                                                                            </button>
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="invoice-form-group invoice-full-width">
                                                    <label className="invoice-label-sm">Description</label>
                                                    <textarea
                                                        className="invoice-input-sm line-item-description"
                                                        placeholder="Description — press Enter for a new line"
                                                        rows={2}
                                                        value={item.description}
                                                        onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                                                    />
                                                </div>
                                                <div className="invoice-form-group">
                                                    <label className="invoice-label-sm">Quantity</label>
                                                    <input
                                                        type="number"
                                                        className="invoice-input-sm"
                                                        placeholder="0"
                                                        value={displayNumberInput(item.quantity)}
                                                        onChange={(e) => handleLineItemChange(
                                                            item.id,
                                                            'quantity',
                                                            e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0
                                                        )}
                                                    />
                                                </div>
                                                <div className="invoice-form-group">
                                                    <label className="invoice-label-sm">Unit Price</label>
                                                    <input
                                                        type="number"
                                                        className="invoice-input-sm"
                                                        placeholder="0"
                                                        value={displayNumberInput(item.unitPrice)}
                                                        onChange={(e) => handleLineItemChange(
                                                            item.id,
                                                            'unitPrice',
                                                            e.target.value === '' ? 0 : parseFloat(e.target.value) || 0
                                                        )}
                                                    />
                                                </div>
                                                <div className="invoice-form-group">
                                                    <label className="invoice-label-sm">Total</label>
                                                    <div className="invoice-total-display">
                                                        {fmtCurrency(item.quantity * item.unitPrice)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button className="invoice-add-item-btn" onClick={handleAddLineItem}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                Add Line Item
                            </button>

                            {/* Pricing Summary */}
                            <div className="invoice-pricing-summary">
                                <div className="invoice-pricing-row">
                                    <span>Subtotal</span>
                                    <span className="invoice-price">{fmtCurrency(subtotal)}</span>
                                </div>
                                <div className="invoice-pricing-row">
                                    <span>Discount</span>
                                    <div className="invoice-price-input-wrapper">
                                        <input
                                            type="number"
                                            className="invoice-price-input"
                                            placeholder="0"
                                            value={displayNumberInput(discount)}
                                            onChange={(e) => setDiscount(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                        />
                                        <span>%</span>
                                    </div>
                                </div>
                                <div className="invoice-pricing-row">
                                    <span>Tax</span>
                                    <div className="invoice-price-input-wrapper">
                                        <input
                                            type="number"
                                            className="invoice-price-input"
                                            placeholder="0"
                                            value={displayNumberInput(tax)}
                                            onChange={(e) => setTax(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                        />
                                        <span>%</span>
                                    </div>
                                </div>
                                <div className="invoice-pricing-row invoice-total-row">
                                    <span>Total</span>
                                    <span className="invoice-total-price">{fmtCurrency(total)}</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Client Message */}
                    <section className="quote-card">
                        <div className="invoice-card-header">
                            <div className="invoice-icon-wrapper invoice-icon-message">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="invoice-card-title">Client Message</h2>
                                <p className="invoice-card-subtitle">Optional message to include</p>
                            </div>
                        </div>

                        <div className="invoice-form-group">
                            <textarea
                                className="invoice-textarea"
                                placeholder="Add a personalized message for your client..."
                                value={clientMessage}
                                onChange={(e) => setClientMessage(e.target.value)}
                                rows={4}
                            />
                        </div>
                    </section>

                    {/* Contract/Disclaimer */}
                    <section className="quote-card">
                        <div className="invoice-disclaimer-header">
                            <h3>Contract / Disclaimer</h3>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M7.5 10L12.5 15L7.5 10ZM12.5 5L7.5 10L12.5 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <p className="invoice-disclaimer-text">{disclaimer}</p>
                    </section>

                    {/* Action Buttons */}
                    <div className="quote-actions">
                        <button
                            className="btn-secondary"
                            onClick={handleSave}
                            disabled={isSaving || isSending || isDownloading}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            className="btn-secondary"
                            onClick={handleDownloadPdf}
                            disabled={isDownloading || isSaving || isSending}
                        >
                            {isDownloading ? 'Downloading...' : 'Download PDF'}
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleReviewAndSend}
                            disabled={isSaving || isSending || isDownloading}
                        >
                            {isSaving ? 'Preparing...' : 'Review and Send'}
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
